/**
 * SupportTicketsPage Component
 *
 * Main page for viewing and managing support tickets.
 */

import React, { useState, useMemo, useEffect } from 'react';
import { ArrowPathIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../../../components/layout/DashboardLayout';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Alert } from '../../../components/ui/Alert';
import { TicketTable } from '../components/TicketTable';
import { CreateTicketModal } from '../components/CreateTicketModal';
import { TicketDetailModal } from '../components/TicketDetailModal';
import { useSupportTickets } from '../hooks/useSupportTickets';
import { useAuth } from '../../../contexts/AuthContext';
import { useLayoutProps } from '../../../hooks/useLayoutProps';
import type { SupportTicketSanitized, SupportTicketStatus, SupportTicketListQuery } from '../../../types/support-ticket.types';
import { statusLabels, SupportTicketStatus as StatusEnum } from '../../../types/support-ticket.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * SupportTicketsPage component
 */
export const SupportTicketsPage: React.FC = () => {
  const { tenant } = useAuth();
  const layoutProps = useLayoutProps();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter state
  const [statusFilter, setStatusFilter] = useState<SupportTicketStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicketSanitized | null>(null);
  const [showDetailModal, setShowDetailModal] = useState<boolean>(false);

  // Track ticket ID from URL for deep-link opens (notification clicks)
  const [deepLinkTicketId, setDeepLinkTicketId] = useState<string | null>(null);

  const tenantId = tenant?.tenantId || '';

  // Build query
  const query: SupportTicketListQuery = useMemo(() => ({
    ...(statusFilter !== 'all' && { status: statusFilter }),
  }), [statusFilter]);

  // Fetch tickets
  const {
    data: ticketsData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useSupportTickets(tenantId, query);

  const tickets = ticketsData?.items || [];

  // Handle ticket ID from URL query param (for notification deep links)
  // Opens modal immediately - TicketDetailModal fetches its own data
  useEffect(() => {
    const ticketParam = searchParams.get('ticket');
    if (ticketParam) {
      setDeepLinkTicketId(ticketParam);
      setShowDetailModal(true);  // Open modal immediately
      // Clear the URL param for a cleaner URL
      setSearchParams((params) => {
        params.delete('ticket');
        return params;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Filter tickets locally for search
  const filteredTickets = useMemo(() => {
    if (!searchQuery) return tickets;

    const query = searchQuery.toLowerCase();
    return tickets.filter(
      (ticket) =>
        ticket.subject.toLowerCase().includes(query) ||
        ticket.referenceNumber.toLowerCase().includes(query) ||
        ticket.createdByName.toLowerCase().includes(query)
    );
  }, [tickets, searchQuery]);

  const handleViewTicket = (ticket: SupportTicketSanitized) => {
    setSelectedTicket(ticket);
    setShowDetailModal(true);
  };

  const handleCloseDetail = () => {
    setShowDetailModal(false);
    setSelectedTicket(null);
    setDeepLinkTicketId(null);
  };

  return (
    <DashboardLayout {...layoutProps}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>Support</h1>
            <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              View and manage your support tickets
            </p>
          </div>
          <Button onClick={() => setShowCreateModal(true)}>
            New Ticket
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1 w-full sm:w-auto">
            <Input
              type="text"
              placeholder="Search tickets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5" style={{ color: 'var(--color-text-muted)' }} />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as SupportTicketStatus | 'all')}
              className="block rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              style={{ borderColor: 'var(--color-border)' }}
            >
              <option value="all">All Statuses</option>
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <Button
            variant="secondary"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="error">
            {getErrorMessage(error)}
          </Alert>
        )}

        {/* Stats */}
        {!isLoading && tickets.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="card rounded-lg shadow px-4 py-3">
              <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Open</p>
              <p className="text-2xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                {tickets.filter((t) => t.status === StatusEnum.OPEN).length}
              </p>
            </div>
            <div className="card rounded-lg shadow px-4 py-3">
              <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>In Progress</p>
              <p className="text-2xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                {tickets.filter((t) => t.status === StatusEnum.IN_PROGRESS).length}
              </p>
            </div>
            <div className="card rounded-lg shadow px-4 py-3">
              <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Awaiting Response</p>
              <p className="text-2xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                {tickets.filter((t) => t.status === StatusEnum.AWAITING_CUSTOMER).length}
              </p>
            </div>
            <div className="card rounded-lg shadow px-4 py-3">
              <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Resolved</p>
              <p className="text-2xl font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                {tickets.filter((t) => t.status === StatusEnum.RESOLVED || t.status === StatusEnum.CLOSED).length}
              </p>
            </div>
          </div>
        )}

        {/* Tickets Table */}
        <TicketTable
          tickets={filteredTickets}
          isLoading={isLoading}
          onViewTicket={handleViewTicket}
        />

        {/* Create Ticket Modal */}
        <CreateTicketModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          tenantId={tenantId}
        />

        {/* Ticket Detail Modal - supports both list click (selectedTicket) and deep link (deepLinkTicketId) */}
        {showDetailModal && (selectedTicket || deepLinkTicketId) && (
          <TicketDetailModal
            isOpen={showDetailModal}
            onClose={handleCloseDetail}
            tenantId={tenantId}
            ticketId={selectedTicket?.ticketId || deepLinkTicketId!}
          />
        )}
      </div>
    </DashboardLayout>
  );
};
